import type { Locale } from "../localization/constants";
import { resolveLocalizedString } from "../localization";
import { compareCatalogCodes } from "./compareCatalogCodes";

export const LEARNING_STATS_OVERVIEW_ID = "_overview";
export const LEARNING_STATS_MIN_SAMPLE = 8;

export const LEARNING_STAT_DIFFICULTIES = ["easy", "medium", "hard"] as const;

export type LearningStatDifficulty =
  (typeof LEARNING_STAT_DIFFICULTIES)[number];

export type LearningCellTone =
  | "empty"
  | "insufficient"
  | "good"
  | "mixed"
  | "poor";

export type LearningCatalogUnit = {
  id: string;
  code: string;
  name: Record<string, string>;
};

export type LearningCatalogQuestionOption = {
  code: string;
  text: Record<string, string>;
};

export type LearningCatalogQuestion = {
  id: string;
  code: string;
  unitId: string;
  difficulty: LearningStatDifficulty | null;
  prompt: Record<string, string>;
  options: LearningCatalogQuestionOption[];
};

export type StoredDifficultyBucket = {
  catalogQuestionCount: number;
  answerCount: number;
  correctCount: number;
  uniqueParticipantCount: number;
};

export type StoredQuestionStats = {
  questionId: string;
  questionCode: string;
  difficulty: LearningStatDifficulty;
  answerCount: number;
  correctCount: number;
  topWrongOptionCode?: string;
  topWrongOptionCount?: number;
};

export type StoredUnitLearningStats = {
  unitCode: string;
  participantCount: number;
  difficulties: Record<LearningStatDifficulty, StoredDifficultyBucket>;
  questions: StoredQuestionStats[];
};

export type StoredLearningOverview = {
  participantCount: number;
  answeredParticipantCount: number;
  answerCount: number;
  correctCount: number;
};

export type LearningQuestionRow = {
  questionId: string;
  questionCode: string;
  difficulty: LearningStatDifficulty;
  prompt: string;
  answerCount: number;
  correctCount: number;
  accuracyPercent: number | null;
  topWrongOptionCode?: string;
  topWrongOptionCount?: number;
  topWrongLabel?: string;
};

export type LearningCell = {
  difficulty: LearningStatDifficulty;
  catalogQuestionCount: number;
  answerCount: number;
  correctCount: number;
  uniqueParticipantCount: number;
  accuracyPercent: number | null;
  tone: LearningCellTone;
  questions: LearningQuestionRow[];
};

export type LearningUnitRow = {
  unitId: string;
  unitCode: string;
  unitName: string;
  cells: Record<LearningStatDifficulty, LearningCell>;
};

export type LearningWeakestCell = {
  unitId: string;
  unitCode: string;
  unitName: string;
  difficulty: LearningStatDifficulty;
  accuracyPercent: number;
};

export type LearningVerdict =
  | { kind: "never" }
  | { kind: "noAnswers" }
  | { kind: "tooLittle" }
  | { kind: "goingWell" }
  | { kind: "weakest"; cell: LearningWeakestCell };

export type LearningOverview = {
  participantCount: number;
  answeredParticipantCount: number;
  answerCount: number;
  correctCount: number;
  accuracyPercent: number | null;
  statsLoaded: boolean;
  weakest: LearningWeakestCell | null;
  verdict: LearningVerdict;
  units: LearningUnitRow[];
};

export type AggregateLearningOverviewInput = {
  units: LearningCatalogUnit[];
  questions: LearningCatalogQuestion[];
  overview: StoredLearningOverview | null;
  statsByUnitId: ReadonlyMap<string, StoredUnitLearningStats>;
  locale: Locale;
};

export const parseLearningDifficulty = (
  raw: unknown
): LearningStatDifficulty | null => {
  if (raw === "QUESTION_DIFFICULTY_EASY" || raw === "easy") {
    return "easy";
  }
  if (raw === "QUESTION_DIFFICULTY_MEDIUM" || raw === "medium") {
    return "medium";
  }
  if (raw === "QUESTION_DIFFICULTY_HARD" || raw === "hard") {
    return "hard";
  }
  return null;
};

export function accuracyPercent(
  correct: number,
  total: number
): number | null {
  if (total <= 0) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round((correct / total) * 100)));
}

export function cellTone(
  catalogQuestionCount: number,
  answerCount: number,
  percent: number | null
): LearningCellTone {
  if (catalogQuestionCount <= 0) {
    return "empty";
  }
  if (answerCount < LEARNING_STATS_MIN_SAMPLE || percent == null) {
    return "insufficient";
  }
  if (percent >= 75) {
    return "good";
  }
  if (percent >= 50) {
    return "mixed";
  }
  return "poor";
}

const emptyBucket = (): StoredDifficultyBucket => ({
  catalogQuestionCount: 0,
  answerCount: 0,
  correctCount: 0,
  uniqueParticipantCount: 0,
});

export function pickWeakestCell(
  units: LearningUnitRow[]
): LearningWeakestCell | null {
  let weakest: LearningWeakestCell | null = null;
  for (const unit of units) {
    for (const difficulty of LEARNING_STAT_DIFFICULTIES) {
      const cell = unit.cells[difficulty];
      if (cell.tone === "empty" || cell.tone === "insufficient") {
        continue;
      }
      if (cell.accuracyPercent == null) {
        continue;
      }
      if (
        weakest == null ||
        cell.accuracyPercent < weakest.accuracyPercent ||
        (cell.accuracyPercent === weakest.accuracyPercent &&
          (compareCatalogCodes(unit.unitCode, weakest.unitCode) < 0 ||
            (unit.unitCode === weakest.unitCode &&
              LEARNING_STAT_DIFFICULTIES.indexOf(difficulty) >
                LEARNING_STAT_DIFFICULTIES.indexOf(weakest.difficulty))))
      ) {
        weakest = {
          unitId: unit.unitId,
          unitCode: unit.unitCode,
          unitName: unit.unitName,
          difficulty,
          accuracyPercent: cell.accuracyPercent,
        };
      }
    }
  }
  return weakest;
}

export function buildLearningVerdict(
  statsLoaded: boolean,
  answerCount: number,
  overallPercent: number | null,
  weakest: LearningWeakestCell | null,
  hasCatalogQuestions: boolean
): LearningVerdict {
  if (!statsLoaded) {
    return { kind: "never" };
  }
  if (!hasCatalogQuestions) {
    return { kind: "never" };
  }
  if (answerCount <= 0) {
    return { kind: "noAnswers" };
  }
  if (weakest == null) {
    return { kind: "tooLittle" };
  }
  if (
    overallPercent != null &&
    overallPercent >= 75 &&
    weakest.accuracyPercent >= 75
  ) {
    return { kind: "goingWell" };
  }
  return { kind: "weakest", cell: weakest };
}

function optionLabel(
  question: LearningCatalogQuestion | undefined,
  optionCode: string | undefined,
  locale: Locale
): string | undefined {
  if (!optionCode) {
    return undefined;
  }
  const option = question?.options.find((item) => item.code === optionCode);
  if (!option) {
    return optionCode;
  }
  return resolveLocalizedString(option.text, locale) || optionCode;
}

function questionRowsForCell(
  storedQuestions: StoredQuestionStats[],
  catalogByCode: Map<string, LearningCatalogQuestion>,
  difficulty: LearningStatDifficulty,
  locale: Locale
): LearningQuestionRow[] {
  return storedQuestions
    .filter((question) => question.difficulty === difficulty)
    .map((question) => {
      const catalog = catalogByCode.get(question.questionCode);
      return {
        questionId: question.questionId,
        questionCode: question.questionCode,
        difficulty: question.difficulty,
        prompt:
          resolveLocalizedString(catalog?.prompt, locale) || question.questionCode,
        answerCount: question.answerCount,
        correctCount: question.correctCount,
        accuracyPercent: accuracyPercent(
          question.correctCount,
          question.answerCount
        ),
        topWrongOptionCode: question.topWrongOptionCode,
        topWrongOptionCount: question.topWrongOptionCount,
        topWrongLabel: optionLabel(
          catalog,
          question.topWrongOptionCode,
          locale
        ),
      };
    });
}

function buildCell(
  catalogQuestionCount: number,
  bucket: StoredDifficultyBucket | undefined,
  questions: LearningQuestionRow[]
): LearningCell {
  const answerCount = bucket?.answerCount ?? 0;
  const correctCount = bucket?.correctCount ?? 0;
  const percent = accuracyPercent(correctCount, answerCount);
  return {
    difficulty: questions[0]?.difficulty ?? "easy",
    catalogQuestionCount,
    answerCount,
    correctCount,
    uniqueParticipantCount: bucket?.uniqueParticipantCount ?? 0,
    accuracyPercent: percent,
    tone: cellTone(catalogQuestionCount, answerCount, percent),
    questions,
  };
}

export function aggregateLearningOverview({
  units,
  questions,
  overview,
  statsByUnitId,
  locale,
}: AggregateLearningOverviewInput): LearningOverview {
  const statsLoaded = overview != null || statsByUnitId.size > 0;
  const catalogByCode = new Map(
    questions.map((question) => [question.code, question])
  );
  const catalogCountByUnitDifficulty = new Map<string, number>();
  for (const question of questions) {
    if (!question.difficulty) {
      continue;
    }
    const key = `${question.unitId}:${question.difficulty}`;
    catalogCountByUnitDifficulty.set(
      key,
      (catalogCountByUnitDifficulty.get(key) ?? 0) + 1
    );
  }

  const sortedUnits = [...units].sort((a, b) =>
    compareCatalogCodes(a.code, b.code)
  );

  const participantCount = overview?.participantCount ?? 0;
  const unitRows: LearningUnitRow[] = sortedUnits.map((unit) => {
    const stored = statsByUnitId.get(unit.id);
    const cells = {} as Record<LearningStatDifficulty, LearningCell>;
    for (const difficulty of LEARNING_STAT_DIFFICULTIES) {
      const catalogQuestionCount =
        catalogCountByUnitDifficulty.get(`${unit.id}:${difficulty}`) ?? 0;
      const bucket = stored?.difficulties[difficulty] ?? emptyBucket();
      const mergedBucket: StoredDifficultyBucket = {
        ...bucket,
        catalogQuestionCount:
          catalogQuestionCount || bucket.catalogQuestionCount,
      };
      cells[difficulty] = {
        ...buildCell(
          mergedBucket.catalogQuestionCount,
          statsLoaded ? mergedBucket : undefined,
          questionRowsForCell(
            stored?.questions ?? [],
            catalogByCode,
            difficulty,
            locale
          )
        ),
        difficulty,
      };
    }
    return {
      unitId: unit.id,
      unitCode: unit.code,
      unitName: resolveLocalizedString(unit.name, locale) || unit.code,
      cells,
    };
  });

  const answerCount = overview?.answerCount ?? 0;
  const correctCount = overview?.correctCount ?? 0;
  const overallPercent = accuracyPercent(correctCount, answerCount);
  const weakest = pickWeakestCell(unitRows);
  const hasCatalogQuestions = questions.some(
    (question) => question.difficulty != null
  );

  return {
    participantCount,
    answeredParticipantCount: overview?.answeredParticipantCount ?? 0,
    answerCount,
    correctCount,
    accuracyPercent: overallPercent,
    statsLoaded,
    weakest,
    verdict: buildLearningVerdict(
      statsLoaded,
      answerCount,
      overallPercent,
      weakest,
      hasCatalogQuestions
    ),
    units: unitRows,
  };
}
